import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/api";

export const get_category = createAsyncThunk(
  "product/get_category",
  async (sellerId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/home/get-categorys/${sellerId}`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch categories' });
    }
  }
);

export const get_products = createAsyncThunk(
  "product/get_products",
  async (sellerId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/home/seller/${sellerId}/products`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch products' });
    }
  }
);

export const get_product = createAsyncThunk(
  "product/get_product",
  async ({sellerId, slug, productId}, { fulfillWithValue, rejectWithValue }) => {
    try {
      let url;
      if (productId) {
        // Use product ID route
        url = `/home/seller/${sellerId}/product/id/${productId}`;
      } else {
        // Use slug route
        url = `/home/seller/${sellerId}/product/${slug}`;
      }
      const { data } = await api.get(url);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch product' });
    }
  }
);

export const price_range_product = createAsyncThunk(
  "product/price_range_product",
  async (sellerId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/home/seller/price-range-latest-product/${sellerId}`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch price range products' });
    }
  }
);

export const get_banners = createAsyncThunk(
  "product/get_banners",
  async (sellerId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/banner/get/${sellerId}`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch banners' });
    }
  }
);

export const query_products = createAsyncThunk(
  "product/query_products",
  async ({sellerId,query}, { fulfillWithValue, rejectWithValue }) => {
    try {
      const url = `/home/seller/${sellerId}/query-products?category=${encodeURIComponent(query.category || '')}&rating=${
          query.rating
      }&lowPrice=${query.low}&highPrice=${query.high}&sortPrice=${
          query.sortPrice
      }&pageNumber=${query.pageNumber}&searchValue=${
          query.searchValue ? encodeURIComponent(query.searchValue) : ""
      }`;
      

      
      const { data } = await api.get(url);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to query products' });
    }
  }
);

export const customer_review = createAsyncThunk(
  "review/customer_review",
  async (info, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await api.post("/home/customer/submit-review", info);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to submit review' });
    }
  }
);

export const get_reviews = createAsyncThunk(
  "review/get_reviews",
  async ({ productId, pageNumber }, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/home/customer/get-reviews/${productId}?pageNo=${pageNumber}`
      );
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch reviews' });
    }
  }
);

export const homeReducer = createSlice({
  name: "home",
  initialState: {
    categorys: [],
    products: [],
    totalProduct: 0,
    parPage: 4,
    latest_product: [],
    topRated_product: [],
    discount_product: [],
    priceRange: {
      low: 0,
      high: 100,
    },
    product: {},
    relatedProducts: [],
    moreProducts: [],
    successMessage: "",
    errorMessage: "",
    totalReview: 0,
    rating_review: [],
    reviews: [],
    banners: [],
    isLoading: true,
    isProductLoading: false,
    isSearching: false,
  },
  reducers: {
    messageClear: (state, _) => {
      state.successMessage = "";
      state.errorMessage = "";
    },
    setLoading: (state, { payload }) => {
      state.isLoading = payload;
    },
    setSearching: (state, { payload }) => {
      state.isSearching = payload;
    },
    clearProductData: (state) => {
      state.product = {};
      state.relatedProducts = [];
      state.moreProducts = [];
      state.totalReview = 0;
      state.rating_review = [];
      state.reviews = [];
    },
    updateProductData: (state, { payload }) => {
      // Update product data without clearing other data
      if (payload.product) {
        state.product = payload.product;
      }
      if (payload.relatedProducts) {
        state.relatedProducts = payload.relatedProducts;
      }
      if (payload.moreProducts) {
        state.moreProducts = payload.moreProducts;
      }
      state.isLoading = false;
    }
  },
  extraReducers: {
    [get_category.fulfilled]: (state, { payload }) => {
      state.categorys = payload?.categorys || [];
    },
    [get_products.pending]: (state) => {
      state.isLoading = true;
    },
    [get_products.fulfilled]: (state, { payload }) => {
      state.products = payload?.products || [];
      state.latest_product = payload?.latest_product || [];
      state.topRated_product = payload?.topRated_product || [];
      state.discount_product = payload?.discount_product || [];
      state.isLoading = false;
    },
    [get_products.rejected]: (state) => {
      state.isLoading = false;
    },
    [get_product.pending]: (state) => {
      // Only toggle the dedicated product loading flag
      state.isProductLoading = true;
    },
    [get_product.fulfilled]: (state, { payload }) => {
      // Always set product loading to false when the request completes
      state.isProductLoading = false;
      
      // Only update product data if we have valid product data
      const hasValidProduct = payload?.product && 
                             payload.product._id && 
                             payload.product.name && 
                             payload.product.price !== undefined;
      
      if (hasValidProduct) {
        state.product = payload.product;
        state.relatedProducts = payload.relatedProducts || [];
        state.moreProducts = payload.moreProducts || [];
      } else {
        // If no valid product data, clear the product
        state.product = {};
        state.relatedProducts = [];
        state.moreProducts = [];
      }
    },
    [get_product.rejected]: (state) => {
      state.isProductLoading = false;
      state.product = {};
    },
    [price_range_product.pending]: (state) => {
      state.isLoading = true;
    },
    [price_range_product.fulfilled]: (state, { payload }) => {
      state.latest_product = payload?.latest_product || [];
      state.priceRange = payload?.priceRange || { low: 0, high: 100 };
      state.isLoading = false;
    },
    [price_range_product.rejected]: (state) => {
      state.isLoading = false;
    },
    [query_products.pending]: (state) => {
      state.isSearching = true;
    },
    [query_products.fulfilled]: (state, { payload }) => {
      state.products = payload?.products || [];
      state.totalProduct = payload?.totalProduct || 0;
      state.parPage = payload?.parPage || 4;
      state.isSearching = false;
    },
    [query_products.rejected]: (state) => {
      state.isSearching = false;
    },
    [customer_review.fulfilled]: (state, { payload }) => {
      state.successMessage = payload?.message || 'Review submitted successfully';
    },
    [get_reviews.fulfilled]: (state, { payload }) => {
      state.reviews = payload?.reviews || [];
      state.totalReview = payload?.totalReview || 0;
      state.rating_review = payload?.rating_review || [];
    },
    [get_banners.pending]: (state) => {
      state.isLoading = true;
    },
    [get_banners.fulfilled]: (state, { payload }) => {
      state.banners = payload?.banner || [];
      state.isLoading = false;
    },
    [get_banners.rejected]: (state) => {
      state.isLoading = false;
    },
  },
});

export const { messageClear, setLoading, setSearching, clearProductData, updateProductData } = homeReducer.actions;
export default homeReducer.reducer;
